import React, { useState, useEffect , useRef} from "react";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";
import axios from "axios";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const statusOptions = [" ","D/O", "P", "A", "M", "E", "N", "N/O", "E/O", "Ramzan", "24 Hrs Duty", "Eid Duty", "M+E", "Public Holiday", "L"];

// 🔹 Urdu translation map for duty codes
const dutyMapUrdu = {
  M: "صبح",
  E: "دوپہر",
  N: "رات",
  "D/O": "چھٹی",
  P: "حاضر",
  A: "غیر حاضر",
  "N/O": "رات آف",
  "E/O": "دوپہر آف",
  Ramzan: "رمضان",
  "24 Hrs Duty": "چوبیس گھنٹے ڈیوٹی",
  "Eid Duty": "عید ڈیوٹی",
  "M+E": "صبح + دوپہر",
};

// 🔹 Helper: auto translate to Urdu
const autoTranslateToUrdu = async (text) => {
  if (!text?.trim()) return text;
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(
        text
      )}`
    );
    const json = await res.json();
    return json[0]?.map((item) => item[0]).join("") || text;
  } catch (err) {
    console.error("Translation error:", err);
    return text;
  }
};

const generatePDFBlob = async ({
  language,
  doctors,
  month,
  days,
  staff,
  numDays,
  selectedCategory,
}) => {
  const isUrdu = language === "ur";

 const categoryTitles = {
  SR: isUrdu ? "سینئر رجسٹرار" : "Senior Registrar",
  AP: isUrdu ? "اسسٹنٹ پروفیسر" : "Assistant Professor",
  MO: isUrdu ? "میڈیکل آفیسر" : "Medical Officer", // ✅ Added
  PG: isUrdu ? "پوسٹ گریجویٹس" : "Post Graduates",
};
  const categoryTitle = categoryTitles[selectedCategory] || "";

  const hospitalName = isUrdu
    ? "راولپنڈی انسٹی ٹیوٹ آف کارڈیالوجی"
    : "Rawalpindi Institute of Cardiology";
  const subAddress = isUrdu ? "راول روڈ، راولپنڈی" : "Rawal Road, Rawalpindi";

  const title = categoryTitle
    ? `${categoryTitle} ${isUrdu ? "کی حاضری" : "Attendance"}`
    : isUrdu
    ? "ڈاکٹرز کی حاضری"
    : "Doctors Attendance";

  const leftLogoUrl = "../../assets/img/logo/new-logo.png";
  const rightLogoUrl = "../../assets/img/logo/punjab-logo.png";

  const currentYear = new Date().getFullYear();
  const sundays = [];
  for (let i = 1; i <= numDays; i++) {
    const date = new Date(`${month} ${i}, ${currentYear}`);
    if (date.getDay() === 0) sundays.push(i);
  }

  // 🧱 Header HTML (we will insert this before each new table page)
  const tableHeaderHTML = (() => {
    const dayHeaders = Array.from({ length: numDays }, (_, i) => {
      const dayNum = i + 1;
      const isSunday = sundays.includes(dayNum);
      return `<th style="padding:6px; border:1px solid #ccc; ${
        isSunday
          ? "background:#222; color:#fff; font-weight:700;"
          : "background:#eaf1ff; color:#333;"
      }">${dayNum}</th>`;
    }).join("");

    return `
      <thead>
        <tr>
          <th style="padding:6px; border:1px solid #ccc; background:#0052D4; color:#fff;">
            ${isUrdu ? "عملے کا نام" : "Name of Doctors"}
          </th>
          ${dayHeaders}
          <th style="padding:6px; border:1px solid #ccc; background:#cfe2ff;">
            ${isUrdu ? "خلاصہ" : "Summary"}
          </th>
        </tr>
      </thead>
    `;
  })();

// 🧾 Build All Rows
const rowsHtml = await Promise.all(
  staff.map(async (personName) => {
    const displayName = isUrdu ? await autoTranslateToUrdu(personName) : personName;

    const rowCells = Array.from({ length: numDays }, (_, i) => {
      const dayNum = i + 1;
      const isSunday = sundays.includes(dayNum);

      // ✅ Preserve true blank values
      let value = days[personName]?.[dayNum];
      if (value === undefined || value === null) value = ""; // keep blank

      if (isUrdu && dutyMapUrdu[value]) value = dutyMapUrdu[value];

      // ✅ Apply color coding
      let displayValue = value;
      if (value === "P") displayValue = `<span style='color:green; font-weight:600;'>P</span>`;
      if (value === "A") displayValue = `<span style='color:red; font-weight:600;'>A</span>`;
      if (value === "L") displayValue = `<span style='color:blue; font-weight:600;'>L</span>`;

      // ✅ Keep cell empty if blank
      return `<td style="padding:6px; border:1px solid #ccc; ${
        isSunday ? "background:#f5f5f5; font-weight:600;" : ""
      }">${displayValue === "" ? "&nbsp;" : displayValue}</td>`;
    }).join("");


      // Attendance summary
      const attendance = days[personName] || {};
      const values = Object.values(attendance);
      const validDays = values.filter((v) => v !== "D/O");
      const totalP = validDays.filter((v) => v === "P").length;
      const totalA = validDays.filter((v) => v === "A").length;
      const totalL = validDays.filter((v) => v === "L").length; // ✅ count leaves
      const totalCount = validDays.length || 1;
      const percentage = ((totalP / totalCount) * 100).toFixed(1);


      let color = "#dc3545";
      if (percentage >= 80) color = "#28a745";
      else if (percentage >= 60) color = "#ffc107";

      return `
        <tr>
          <td style="padding:6px; border:1px solid #ccc; font-weight:600;">${displayName}</td>
          ${rowCells}
          <td style="padding:6px; border:1px solid #ccc; background:#f0f7ff; font-weight:600; color:${color};">
            <span style="color:green;">P:</span> ${totalP} |
            <span style="color:red;">A:</span> ${totalA} |
            <span style="color:blue;">L:</span> ${totalL} <br>
            (${percentage}%)
          </td>

        </tr>
      `;
    })
  );

  // 🧩 Chunk rows per page (~14 rows per page)
  const chunkSize = 14;
  const paginatedTables = [];
  for (let i = 0; i < rowsHtml.length; i += chunkSize) {
    const chunk = rowsHtml.slice(i, i + chunkSize).join("");
    const table = `
      <div style="page-break-before:${i === 0 ? "auto" : "always"};">
        <table style="width:100%; border-collapse:collapse; text-align:center; font-size:9px;">
          ${tableHeaderHTML}
          <tbody>${chunk}</tbody>
        </table>
      </div>
    `;
    paginatedTables.push(table);
  }

  const allTablesHTML = paginatedTables.join("");

  // 🏥 PDF Content
  const content = `
    <div style="font-family:${
      isUrdu ? "'Noto Nastaliq Urdu'" : "'Segoe UI', sans-serif"
    }; direction:${isUrdu ? "rtl" : "ltr"}; padding:20px; font-size:12px; color:#2c3e50;">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:20px;">
        <div style="width:25%;"><img src="${leftLogoUrl}" style="height:60px;" /></div>
        <div style="text-align:center; width:60%;">
          <h2 style="margin:0; font-weight:700;">${hospitalName}</h2>
          <p style="margin:2px 0; font-size:13px; color:#555;">${subAddress}</p>
          <h4 style="margin:6px 0; color:#444;">${title} – ${month}</h4>
        </div>
        <div style="width:25%; text-align:${isUrdu ? "left" : "right"};">
          <img src="${rightLogoUrl}" style="height:60px;" />
        </div>
      </div>

      ${allTablesHTML}

      <p style="text-align:center; margin-top:20px; font-size:10px; color:#888;">
        © ${currentYear} ${hospitalName}. ${
    isUrdu ? "جملہ حقوق محفوظ ہیں۔" : "All rights reserved."
  }
      </p>
    </div>
  `;

  return await html2pdf()
    .from(content)
    .set({
      margin: 0.3,
      filename: `Doctors_Attendance_${month}_${language.toUpperCase()}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
    })
    .output("blob");
};



const DoctorsAttendance = (language) => {
  const [staffList, setStaffList] = useState([]);
  const [staff, setStaff] = useState([]); // Selected staff
  const [numDays, setNumDays] = useState(30);
    const [selectedDepartment, setSelectedDepartment] = useState("doctorsattendance");
    const [department, setDepartment] = useState("doctorsattendance");
  
   const [month, setMonth] = useState(""); // start empty, load only on user selection
  const [days, setDays] = useState({});
  const [newStaffName, setNewStaffName] = useState("");
  const [newStaffRole, setNewStaffRole] = useState("");
  const isUrdu = language === "ur";
  const [selectedCategory, setSelectedCategory] = useState("");
const [newStaffDesignation, setNewStaffDesignation] = useState(null);
const [designationOptions, setDesignationOptions] = useState([
  { value: "Assistant Professor", label: "Assistant Professor" },
  { value: "Senior Registrar", label: "Senior Registrar" },
  { value: "Medical Officer", label: "Medical Officer" },
  { value: "Post Graduates", label: "Post Graduates" }
  
  
]);
const [hoveredRow, setHoveredRow] = useState(null);
const [hoveredCol, setHoveredCol] = useState(null);
  const [filterName, setFilterName] = useState("");
  const [eCount, setECount] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);

 const countEForStaff = (name) => {
  if (!name || !days[name]) {
    setECount(0);
    return;
  }

  const attendance = days[name]; // { "1": "P", "2": "E", ... }
  const totalE = Object.values(attendance).filter(status => status === "A").length;
  setECount(totalE);
};

  
  const [roles, setRoles] = useState([
    { value: "Ward Boy", label: "Ward Boy" },
    { value: "Aya", label: "Aya" },
    { value: "Other", label: "➕ Add New Role" },
  ]);

  const [highlightIndex, setHighlightIndex] = useState(-1);

  // Inside your component
const inputRef = useRef(null);
const [showSuggestions, setShowSuggestions] = useState(false);

  const roleOptions = [
  { value: "Ward Boy", label: "Ward Boy" },
  { value: "Aya", label: "Aya" }
];

const addStaff = () => {
  if (!newStaffName.trim() || !newStaffRole) {
    return Swal.fire("⚠️ Please enter name and role", "", "warning");
  }

  // Prevent duplicate staff
  const exists = staffList.some(
    (s) => s.name.toLowerCase() === newStaffName.toLowerCase()
  );
  if (exists) {
    return Swal.fire(
      "⚠️ Already Exists",
      "This staff member is already in the list.",
      "warning"
    );
  }

  axios.post("https://ricgop.online/backend/staff/api/add-staff.php", {
    name: newStaffName,
    designation: newStaffDesignation.value, // ✅ matches DB
    category: selectedCategory,
  })
  .then(() => {
    Swal.fire("✅ Added!", "Staff saved successfully", "success");

    // Add staff to staffList
    setStaffList(prev => [
      ...prev,
      { name: newStaffName, role: newStaffRole.value || newStaffRole },
    ]);

    // Add staff to selected staff and generate default days if month is selected
    setStaff(prev => {
      if (prev.includes(newStaffName)) return prev;
      return [...prev, newStaffName];
    });

    setDays(prevDays => {
      if (!month) return prevDays; // if month not selected, don't generate days
      const newDays = { ...prevDays };
      if (!newDays[newStaffName]) {
        const defaultDays = generateDefaultDays([newStaffName], month, numDays)[newStaffName];
        newDays[newStaffName] = defaultDays;
      }
      return newDays;
    });

    setNewStaffName("");
    setNewStaffRole(null);
  })
  .catch(() => {
    Swal.fire("❌ Error", "Failed to add staff", "error");
  });
};


  useEffect(() => {
  if (month) fetchRoster(null, month);
}, [month]);

  useEffect(() => {
  if (month) {
    // Call your fetchRoster function whenever month changes
    fetchRoster(null, month);
  }
}, [month]);

useEffect(() => {
  const fetchDoctors = async () => {
    try {
      const res = await axios.get("https://ricgop.online/backend/staff/api/get-staff.php");
      if (Array.isArray(res.data)) {
        setStaffList(res.data.map(doc => ({
          name: doc.name,
          designation: doc.designation || "",
          category: doc.category || ""
        })));
      } else {
        setStaffList([]);
      }
    } catch (err) {
      console.error("Failed to fetch doctors:", err);
      setStaffList([]);
    }
  };
  fetchDoctors();
}, []);



useEffect(() => {
  if (!month) return;

  // month expected like "January 2026"
  const parts = month.trim().split(" ");
  const yearStr = parts.pop();
  const monthName = parts.join(" ");
  const yearNum = parseInt(yearStr, 10);

  if (!monthName || isNaN(yearNum)) return;

  const monthIndex = new Date(`${monthName} 1, ${yearNum}`).getMonth();
  if (isNaN(monthIndex)) return;

  const daysInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();
  setNumDays(daysInMonth);
}, [month]);

// 2) Sync the `days` object when staff / month / numDays change.
//    Adds default entries for newly-required days and trims extras.
useEffect(() => {
  if (!month || staff.length === 0) return;

  setDays(prev => {
    const next = { ...prev };

    const parts = month.trim().split(" ");
    const yearStr = parts.pop();
    const monthName = parts.join(" ");
    const yearNum = parseInt(yearStr, 10);
    const monthIndex = new Date(`${monthName} 1, ${yearNum}`).getMonth();

    staff.forEach(person => {
      if (!next[person]) next[person] = {};

      // Add any missing days up to numDays with your default logic
      for (let d = 1; d <= numDays; d++) {
        if (next[person][d] === undefined) {
          const date = new Date(yearNum, monthIndex, d);
          next[person][d] = date.getDay() === 0 ? "D/O" : "A";
        }
      }

      // Remove extra days if numDays decreased
      Object.keys(next[person]).forEach(k => {
        const dayNum = Number(k);
        if (dayNum > numDays) delete next[person][k];
      });
    });

    return next;
  });
}, [numDays, staff, month]);

const holidayMap = {
  "March 2025": { 31: "Eid-ul-Fitr" },
  "April 2025": { 1: "Eid-ul-Fitr", 2: "Eid-ul-Fitr" },
  "June 2025": { 7: "Eid-ul-Adha", 8: "Eid-ul-Adha", 9: "Eid-ul-Adha" },
  "September 2025": {
    5: "Eid Milad-un-Nabi",
    6: "Defence Day",
    7: "Air Force Day",
  },
  // Add other years/months as needed
};

const generateDefaultDays = (staffArray, monthStr, numDays) => {
  const temp = {};
  staffArray.forEach((person) => {
    temp[person] = {};
    for (let i = 1; i <= numDays; i++) {
      const [monthName, year] = monthStr.split(" ");
      const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();
      const date = new Date(year, monthIndex, i);
      temp[person][i] = date.getDay() === 0 ? "D/O" : "A"; // Sunday = D/O
    }
  });
  return temp;
};


  const updateDay = (name, dayNum, value) => {
    setDays(prev => ({
      ...prev,
      [name]: { ...prev[name], [dayNum]: value }
    }));
  };


  useEffect(() => {
  const handleClickOutside = (event) => {
    if (inputRef.current && !inputRef.current.contains(event.target)) {
      setShowSuggestions(false); // close dropdown
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);

  const downloadPDFs = async () => {
    if (!staff.length) return Swal.fire("⚠️ Select at least one staff member", "", "warning");
    Swal.fire({ title: 'Generating...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
const [enBlob, urBlob] = await Promise.all([
  generatePDFBlob({ language: "en", doctors: staffList, month, days, staff, numDays, selectedCategory }),
  generatePDFBlob({ language: "ur", doctors: staffList, month, days, staff, numDays, selectedCategory })
]);


      const link = document.createElement("a");
      link.href = URL.createObjectURL(enBlob);
      link.download = `Doctors_Attendance_${month}_EN.pdf`;
      link.click();

      const link2 = document.createElement("a");
      link2.href = URL.createObjectURL(urBlob);
      link2.download = `Doctors_Attendance_${month}_UR.pdf`;
      link2.click();

      Swal.fire("✅ Success", "PDFs downloaded.", "success");
    } catch {
      Swal.fire("❌ Error", "PDF generation failed.", "error");
    }
  };

const handleStaffSelect = (option) => {
  setSelectedStaff(option);
  
  if (!option || !days[option.value]) {
    setECount(0);
    return;
  }

  const attendance = days[option.value]; // { "1": "P", "2": "E", ... }
  const totalE = Object.values(attendance).filter(status => status === "A").length;
  setECount(totalE);
};


// ✅ Change all doctors' status for a specific day
const updateAllForDay = (dayNum, value) => {
  setDays(prev => {
    const updated = { ...prev };
    Object.keys(updated).forEach(name => {
      if (updated[name]) {
        updated[name][dayNum] = value;
      }
    });
    return updated;
  });
};

// ✅ Change all doctors' status for ALL days
const updateAllDaysForAllDoctors = (value) => {
  setDays(prev => {
    const updated = {};
    Object.keys(prev).forEach(name => {
      updated[name] = {};
      Object.keys(prev[name]).forEach(day => {
        updated[name][day] = value;
      });
    });
    return updated;
  });
};


const handleExcelUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // ✅ Check that both department and category are selected before upload
  if (!selectedDepartment || !selectedCategory) {
    Swal.fire("⚠️ Missing Info", "Please select Department and Category first.", "warning");
    return;
  }

  const XLSX = await import("xlsx");
  Swal.fire({
    title: "📊 Reading Excel...",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // Read as array (raw rows)
    const json = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" });

    // ✅ Use row 3 as header (B3 = Name of Employees, C3 = Day 1, etc.)
    const headers = (json[2] || []).map((h) =>
      String(h)
        .replace(/\u00A0/g, " ")
        .replace(/[^\x20-\x7E]/g, "")
        .trim()
    );
    const rows = json.slice(3); // Start from row 4

    console.log("🧾 Headers (row 3):", headers);

    // --- Normalize helper
    const normalize = (txt) =>
      String(txt)
        .normalize("NFKD")
        .replace(/\u00A0/g, "")
        .toLowerCase()
        .replace(/\s+/g, "")
        .replace(/[^a-z0-9]/g, "");

    // --- Detect columns
    const nameColIndex = headers.findIndex((h) => {
      const norm = normalize(h);
      return (
        norm.includes("name") ||
        norm.includes("employee") ||
        norm.includes("employees") ||
        norm.includes("staff")
      );
    });

    const designationColIndex = headers.findIndex((h) => {
      const norm = normalize(h);
      return (
        norm.includes("designation") ||
        norm.includes("title") ||
        norm.includes("position")
      );
    });

    if (nameColIndex === -1) {
      Swal.fire(
        "❌ Error",
        "Excel must have a column like 'Name of Employees' (in row 3, column B)",
        "error"
      );
      return;
    }

    // --- Detect numeric day columns (1, 2, 3, ...)
    const dayCols = headers
      .map((h, i) => {
        const numMatch = String(h).trim().match(/^\d+$/);
        if (numMatch) return { day: numMatch[0], index: i };
        return null;
      })
      .filter(Boolean);

    if (dayCols.length === 0) {
      Swal.fire("❌ Error", "No numeric day columns found (like 1, 2, 3...)", "error");
      return;
    }

    // --- Build parsed data structure
    const parsedDays = {};
    const staffList = [];

    rows.forEach((row) => {
      const staffName = String(row[nameColIndex] ?? "").trim();
      if (!staffName) return;

      // ✅ Fetch designation (either from Excel or fallback)
      let designation = "";
      if (designationColIndex !== -1) {
        designation = String(row[designationColIndex] ?? "").trim();
      }

      // ✅ Fix: always normalize designation properly
      if (!designation || designation.trim() === "" || designation === "-") {
        if (selectedCategory === "AP") designation = "Assistant Professor";
        else if (selectedCategory === "MO") designation = "Medical Officer";
        else if (selectedCategory === "SR") designation = "Senior Registrar";
        else if (selectedCategory === "PG") designation = "Post Graduates";
        else designation = "N/A";
      }

      // ✅ Store attendance
      parsedDays[staffName] = {};
      dayCols.forEach(({ day, index }) => {
        let cellValue = String(row[index] ?? "").trim();
        if (!cellValue) cellValue = "A"; // Blank = Absent
        parsedDays[staffName][day] = cellValue;
      });

      // ✅ Push consistent staff record (important for DB)
      staffList.push({
        name: staffName,
        designation,
        category: selectedCategory,
        department: selectedDepartment,
      });
    });

    console.log("✅ Parsed data with designations:", staffList);

    Swal.update({ title: "💾 Uploading attendance to database..." });

    // ✅ POST to API
    const response = await axios.post(
      "https://ricgop.online/backend/staff/api/store-roster.php",
      {
        department: selectedDepartment,
        category: selectedCategory,
        month,
        numDays,
        days: parsedDays,
        staffList,
      },
      { headers: { "Content-Type": "application/json" } }
    );

    if (response.data.success) {
      Swal.fire("✅ Success", "Excel uploaded successfully!", "success");
      setDays(parsedDays);
      setStaff(Object.keys(parsedDays));
    } else {
      Swal.fire("❌ Error", response.data.message || "Upload failed", "error");
      console.error("Server response:", response.data);
    }
  } catch (err) {
    console.error("Excel Upload Error:", err);
    Swal.fire("❌ Error", "Invalid Excel format or upload failed", "error");
  }
};



   // Handle role selection
  const handleRoleChange = (selectedRole) => {
    if (selectedRole.value === "Other") {
      Swal.fire({
        title: "Enter New Role",
        input: "text",
        inputPlaceholder: "Enter role name",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed && result.value.trim() !== "") {
          const newRole = { value: result.value, label: result.value };
          setRoles((prev) => [
            ...prev.filter((r) => r.value !== "Other"),
            newRole,
            { value: "Other", label: "➕ Add New Role" },
          ]);
          setNewStaffRole(newRole);
        }
      });
    } else {
      setNewStaffRole(selectedRole);
    }
  };

  // Filtered suggestions
  const filteredSuggestions = staffList
    .filter((st) => st.name.toLowerCase().includes(newStaffName.toLowerCase()))
    .slice(0, 5);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev < filteredSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) =>
        prev > 0 ? prev - 1 : filteredSuggestions.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0 && filteredSuggestions[highlightIndex]) {
        setNewStaffName(filteredSuggestions[highlightIndex].name);
        setShowSuggestions(false);
        setHighlightIndex(-1);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setHighlightIndex(-1);
    }
  };

  

const saveRosterToDB = () => {
  if (!staff.length) {
    Swal.fire("⚠️ No staff selected", "Please add or select staff before saving", "warning");
    return;
  }

  Swal.fire({ title: "Saving Attendnace...", allowOutsideClick: false, didOpen: () => Swal.showLoading() });

  // 🧹 Filter out removed staff from days before sending
  const cleanedDays = Object.keys(days)
    .filter(name => staff.includes(name)) // only keep selected staff
    .reduce((obj, name) => {
      obj[name] = days[name];
      return obj;
    }, {});

  axios.post("https://ricgop.online/backend/staff/api/add-roster.php", {
  department,
  category: selectedCategory, // ✅ include new dropdown value
  month,
  numDays,
  days: cleanedDays,
  })
  .then(res => {
    if (res.data.success) {
      Swal.fire("✅ Saved!", `Roster ID: ${res.data.roster_id}`, "success");
    } else {
      Swal.fire("❌ Error", res.data.message, "error");
    }
  })
  .catch(() => {
    Swal.fire("❌ Error", "Failed to save roster", "error");
  });
};




const fetchRoster = (rosterId = null, monthValue = null) => {
  const department = selectedDepartment?.toLowerCase(); // e.g. "ecg", "hvac"
  if (!department) {
    Swal.fire("⚠️ Error", "Department not selected", "error");
    return;
  }

  let url = `https://ricgop.online/backend/staff/api/view-roster.php?department=${department}`;

if (selectedCategory) {
  url += `&category=${encodeURIComponent(selectedCategory)}`;
}

  if (rosterId) {
    url += `&roster_id=${rosterId}`;
  } else if (monthValue) {
    url += `&month=${encodeURIComponent(monthValue)}`;
  }

  Swal.fire({
    title: "Loading roster...",
    allowOutsideClick: false,
    didOpen: () => Swal.showLoading(),
  });

  axios.get(url)
    .then(res => {
  if (res.data.success) {
    const rosterEntries = Object.values(res.data.data);
    if (rosterEntries.length > 0) {
      const roster = rosterEntries[0]; // pick the first roster
      setMonth(roster.month);
      setNumDays(roster.num_days);
      setDays(roster.staffs);
      setStaff(Object.keys(roster.staffs));
    } else {
      setDays({});
      setStaff([]);
    }
  } else {
    setDays({});
    setStaff([]);
  }
  Swal.close();
})
    .catch(() => {
      Swal.fire("❌ Error", "Failed to fetch roster", "error");
    });
};

return (
  <div className="container-fluid bg-white p-5 rounded-4 shadow-lg border " id="expand-view">
    <h3 className="text-2xl font-bold text-center mb-5 text-gray-800">
      🩺 Doctors Attendance
    </h3>

    {/* Month + Days */}
  {/* Role Category + Month */}
<div className="row g-4 mb-4">
  {/* 🔹 New Dropdown: SR/AP/PG */}
<div className="col-md-3">
  <label className="form-label fw-semibold text-gray-700">👨‍🏫 Category</label>
  <select
    className="form-select rounded-3 shadow-sm"
    value={selectedCategory}
    onChange={(e) => {
      const value = e.target.value;
      setSelectedCategory(value);

      // 🔹 Auto-update Designation when Category changes
      if (value === "SR") {
        setNewStaffDesignation({ value: "Senior Registrar", label: "Senior Registrar" });
      } else if (value === "AP") {
        setNewStaffDesignation({ value: "Assistant Professor", label: "Assistant Professor" });
        } else if (value === "MO") {
        setNewStaffDesignation({ value: "Medical Officer", label: "Medical Officer" });
      } else if (value === "PG") {
        setNewStaffDesignation({ value: "Post Graduates", label: "Post Graduates" });
      } else {
        setNewStaffDesignation(null);
      }
    }}
  >
    <option value="">-- Select Category --</option>
    <option value="MO">MO – Medical Officer</option>
    <option value="SR">SR – Senior Registrar</option>
    <option value="AP">AP – Assistant Professor</option>
    <option value="PG">PG’s</option>
  </select>
</div>


  {/* Existing Month Selector */}
  {/* <div className="col-md-3">
    <label className="form-label fw-semibold text-gray-700">📅 Month</label>
    <select
      className="form-select rounded-3 shadow-sm"
      value={month}
      onChange={(e) => {
        const selectedMonth = e.target.value;
        setMonth(selectedMonth);
        if (selectedMonth) fetchRoster(null, selectedMonth);
      }}
      disabled={!selectedCategory} // 👈 prevent month selection until category chosen
    >
      <option value="">-- Select Month --</option>
      {Array.from({ length: 12 }, (_, i) => {
        const d = new Date();
        d.setMonth(d.getMonth() + i);
        const label = d.toLocaleString("default", {
          month: "long",
          year: "numeric",
        });
        return (
          <option key={i} value={label}>
            {label}
          </option>
        );
      })}
    </select>
  </div> */}

  {/* Recent 6 Months Selector */}
<div className="col-md-3">
  <label className="form-label fw-semibold text-gray-700">📅 Month</label>
  <select
    className="form-select rounded-3 shadow-sm"
    value={month}
    onChange={(e) => {
      const selectedMonth = e.target.value;
      setMonth(selectedMonth);
      if (selectedMonth) fetchRoster(null, selectedMonth);
    }}
    disabled={!selectedCategory} // 👈 prevent month selection until category chosen
  >
    <option value="">-- Select Month --</option>
    {Array.from({ length: 6 }, (_, i) => {
      const d = new Date();
      d.setMonth(d.getMonth() - i); // 👈 go backward from current month
      const label = d.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
      return (
        <option key={i} value={label}>
          {label}
        </option>
      );
    }).reverse()} {/* 👈 oldest → newest order */}
  </select>
</div>




{/* <div className="col-md-3">
  <label className="form-label fw-semibold text-gray-700">🗓 Number of Days</label>
  <input
    type="number"
    className="form-control rounded-3 shadow-sm"
    min={1}
    max={31}
    value={numDays}
    disabled // 👈 disables editing
    readOnly // 👈 extra safety
  />
</div> */}
    </div>

    {/* Select Staff */}
    <div className="mb-4">
      <label className="form-label fw-semibold text-gray-700">👨‍⚕️ Select Staff</label>
      <Select
        isMulti
        options={staffList.map(st => ({ value: st.name, label: `${st.name} – ${st.designation}` }))}
        value={staff.map(name => ({
          value: name,
          label: `${name} – ${staffList.find(s => s.name === name)?.designation || ""}`
        }))}
        onChange={(selected) => setStaff(selected.map(opt => opt.value))}
        placeholder="Choose staff..."
        classNamePrefix="select"
      />
    </div>

{/* 🧑‍⚕️ Add Doctor/Staff Form */}
<div
  className="mb-5 position-relative p-3 rounded-3 bg-gray-50 shadow-sm"
  ref={inputRef}
>
  {/* Staff Name */}
  <label className="form-label fw-semibold text-gray-700">
    👤 Staff Name
  </label>
  <input
    type="text"
    className="form-control mb-2 rounded-3 shadow-sm"
    placeholder="Enter staff name"
    value={newStaffName}
    onChange={(e) => {
      setNewStaffName(e.target.value);
      setShowSuggestions(true);
      setHighlightIndex(-1);
    }}
    onFocus={() => setShowSuggestions(true)}
    onKeyDown={handleKeyDown}
  />

  {/* 🔍 Suggestions Dropdown */}
  {showSuggestions && newStaffName && (
    <ul
      className="list-group position-absolute w-100 shadow border rounded-3"
      style={{
        zIndex: 1000,
        maxHeight: "200px",
        overflowY: "auto",
      }}
    >
      {filteredSuggestions.map((st, idx) => {
        const lowerName = st.name.toLowerCase();
        const lowerInput = newStaffName.toLowerCase();
        const startIndex = lowerName.indexOf(lowerInput);
        let before = "",
          match = st.name,
          after = "";
        if (startIndex >= 0) {
          before = st.name.slice(0, startIndex);
          match = st.name.slice(startIndex, startIndex + newStaffName.length);
          after = st.name.slice(startIndex + newStaffName.length);
        }
        return (
          <li
            key={idx}
            className={`list-group-item list-group-item-action ${
              idx === highlightIndex ? "active" : ""
            }`}
            onClick={() => {
              setNewStaffName(st.name);
              setShowSuggestions(false);
              setHighlightIndex(-1);
            }}
            style={{ cursor: "pointer" }}
          >
            {before}
            <strong className="text-primary">{match}</strong>
            {after}
            <span className="text-muted"> – {st.designation}</span>
          </li>
        );
      })}
    </ul>
  )}

  {/* Staff Designation */}
{/* Staff Designation */}
<label className="form-label fw-semibold text-gray-700 mt-3">
  🏥 Designation
</label>
<CreatableSelect
  isClearable
  options={designationOptions}
  value={newStaffDesignation}
  onChange={(selected) => {
    setNewStaffDesignation(selected);

    // 🔹 Auto-update category based on designation
    if (selected) {
      const designation = selected.value.toLowerCase();
      if (designation.includes("Senior Registrar")) {
        setSelectedCategory("SR");
      } else if (designation.includes("Medical Officer")) {
        setSelectedCategory("MO");
      } else if (designation.includes("Assistant Professor")) {
        setSelectedCategory("AP");
      } else if (designation.includes("pg") || designation.includes("Post Graduates")) {
        setSelectedCategory("PG");
      } else {
        setSelectedCategory(""); // default
      }
    } else {
      setSelectedCategory("");
    }
  }}
  placeholder="Select or create designation..."
  className="basic-select"
  classNamePrefix="select"
/>



  {/* ➕ Add Staff Button */}
  <button
    className="btn btn-success mt-3 shadow-sm"
    disabled={
      !newStaffName ||
      !newStaffDesignation ||
      staffList.some(
        (st) => st.name.toLowerCase() === newStaffName.toLowerCase()
      )
    }
    onClick={() => {
      if (!newStaffName || !newStaffDesignation) {
        Swal.fire("⚠️ Please enter name and designation");
        return;
      }

      const exists = staffList.some(
        (s) => s.name.toLowerCase() === newStaffName.toLowerCase()
      );
      if (exists) {
        Swal.fire(
          "⚠️ Already Exists",
          "This staff member is already in the list.",
          "warning"
        );
        return;
      }

      Swal.fire({
        title: "Adding staff...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      axios
        .post("https://ricgop.online/backend/staff/api/add-staff.php", {
          name: newStaffName,
          designation: newStaffDesignation.value, // ✅ maps to DB column
          category: selectedCategory, // SR/AP/PG or similar
        })
        .then((res) => {
          Swal.close();
          if (res.data.success) {
            Swal.fire(
              "✅ Added!",
              res.data.message || "Staff saved successfully",
              "success"
            );
            setStaffList((prev) => [
              ...prev,
              { name: newStaffName, designation: newStaffDesignation.value },
            ]);
            setStaff((prev) =>
              prev.includes(newStaffName)
                ? prev
                : [...prev, newStaffName]
            );
            setNewStaffName("");
            setNewStaffDesignation(null);
          } else {
            Swal.fire(
              "❌ Error",
              res.data.message || "Failed to add staff",
              "error"
            );
          }
        })
        .catch(() => {
          Swal.close();
          Swal.fire("❌ Error", "Failed to add staff", "error");
        });
    }}
  >
    ➕ Add Staff
  </button>
</div>

{/* 🌍 Bulk Update Controls */}
{staff.length > 0 && (
  <div className="bulk-controls-container mb-4 p-3 bg-light rounded shadow-sm border">
    <h5 className="fw-bold mb-3">🧩 Bulk Update Options</h5>

    <div className="row g-3">
      {/* All Days for All Doctors */}
      <div className="col-12 col-md-6">
        <div className="p-3 border rounded bg-white shadow-sm h-100">
          <label className="fw-semibold d-block mb-2">
            🌙 Set All Days for All Doctors:
          </label>
          <Select
            options={statusOptions.map(opt => ({ value: opt, label: opt }))}
            onChange={(selected) => {
              if (selected) {
                Swal.fire({
                  title: `Apply "${selected.value}" to ALL days for ALL doctors?`,
                  icon: "question",
                  showCancelButton: true,
                  confirmButtonText: "Yes, apply it",
                }).then((res) => {
                  if (res.isConfirmed) updateAllDaysForAllDoctors(selected.value);
                });
              }
            }}
            classNamePrefix="select"
            placeholder="Select status..."
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }),
              control: base => ({ ...base, borderRadius: '0.5rem' }),
            }}
          />
        </div>
      </div>

      {/* Single Day for All Doctors */}
      <div className="col-12 col-md-6">
        <div className="p-3 border rounded bg-white shadow-sm h-100">
          <label className="fw-semibold d-block mb-2">
            📅 Apply Status to a Single Day:
          </label>
          <div className="d-flex align-items-center gap-2">
            <input
              type="number"
              min="1"
              max={numDays}
              placeholder="Day #"
              id="bulkDayInput"
              className="form-control w-auto"
              style={{ width: "90px" }}
            />
            <Select
              options={statusOptions.map(opt => ({ value: opt, label: opt }))}
              onChange={(selected) => {
                const dayInput = document.getElementById("bulkDayInput");
                const dayNum = parseInt(dayInput.value);
                if (selected && dayNum >= 1 && dayNum <= numDays) {
                  Swal.fire({
                    title: `Apply "${selected.value}" to Day ${dayNum} for ALL doctors?`,
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Yes, apply it",
                  }).then((res) => {
                    if (res.isConfirmed) updateAllForDay(dayNum, selected.value);
                  });
                } else {
                  Swal.fire("⚠️ Invalid Day", "Please enter a valid day number", "warning");
                }
              }}
              classNamePrefix="select"
              placeholder="Select status..."
              menuPortalTarget={document.body}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                control: base => ({ ...base, borderRadius: '0.5rem' }),
              }}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
)}



{/* Roster Table */}
{/* Roster Table */}
{staff.length > 0 && (
  <div className="roster-wrapper mb-4 overflow-x-auto">
    <table className="roster-table min-w-full border-collapse">
<thead className="sticky top-0 bg-white z-10">
  <tr>
    <th className="px-2 py-2 text-left border bg-gray-100">Staff</th>
    {Array.from({ length: numDays }, (_, i) => (
      <th
  key={i}
  className={`day-pill px-2 py-2 text-center border min-w-[60px]
    ${hoveredCol === i ? "lshape-col" : ""}
  `}
  onMouseEnter={() => setHoveredCol(i)}
  onMouseLeave={() => setHoveredCol(null)}
>
  <span>{i + 1}</span>
</th>

    ))}
    <th className="px-2 py-2 text-center border bg-blue-50">Summary</th>
  </tr>
</thead>

      <tbody>
        {staff.map((person, index) => (
          <tr key={index} className="odd:bg-gray-50">
      <td
  className={`staff-cell px-2 py-2 border whitespace-nowrap
    ${hoveredRow === index ? "lshape-row" : ""}
  `}
  onMouseEnter={() => setHoveredRow(index)}
  onMouseLeave={() => setHoveredRow(null)}
>
  {person}
</td>
            {Array.from({ length: numDays }, (_, i) => {
              // ✅ Preserve true blank values (no fallback)
const rawValue = days[person]?.[i + 1];
const dayValue = rawValue === undefined || rawValue === null ? "" : rawValue;

              return (
       <td
  key={i}
  className={`px-1 py-4 text-center align-middle border min-w-[60px]
    ${hoveredRow === index && i <= hoveredCol ? "lshape-row" : ""}
    ${hoveredCol === i && index <= hoveredRow ? "lshape-col" : ""}
    ${hoveredRow === index && hoveredCol === i ? "lshape-intersect" : ""}
  `}
  style={{
    backgroundColor: holidayMap[month]?.[i + 1]
      ? "#fff8e1"
      : "inherit",
  }}
  onMouseEnter={() => {
    setHoveredRow(index);
    setHoveredCol(i);
  }}
  onMouseLeave={() => {
    setHoveredRow(null);
    setHoveredCol(null);
  }}
>

           <Select
  value={
    dayValue
      ? {
          value: dayValue,
          label: isUrdu && dutyMapUrdu[dayValue] ? dutyMapUrdu[dayValue] : dayValue,
        }
      : null
  }
  onChange={(selected) =>
    updateDay(person, i + 1, selected ? selected.value : "")
  }
 options={[
  { value: "", label: " " }, // ✅ blank option
  ...statusOptions.map((opt) => ({
    value: opt,
    label: isUrdu && dutyMapUrdu[opt] ? dutyMapUrdu[opt] : opt,
  })),
]}

  className="status-dropdown"
  classNamePrefix="select"
  menuPortalTarget={document.body}
  components={{
    ClearIndicator: () => null,
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null,
  }}
  styles={{
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({
      ...base,
      minWidth: "160px",
      direction: isUrdu ? "rtl" : "ltr", // ✅ RTL for Urdu
      textAlign: isUrdu ? "right" : "left",
    }),
    control: (base) => ({
      ...base,
      minHeight: "30px",
      height: "30px",
      fontSize: "12px",
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "0 4px",
      display: "block",
      alignItems: "center",
      justifyContent: "center",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "12px",
      fontWeight: "500",
    }),
    option: (base) => ({
      ...base,
      fontSize: "13px",
      padding: "6px 10px",
      textAlign: isUrdu ? "right" : "left",
    }),
  }}
/>
                  {holidayMap[month]?.[i + 1] && (
                    <div className="holiday-note text-xs text-gray-500 mt-1">
                      {holidayMap[month][i + 1]}
                    </div>
                  )}
                </td>
              );
            })}
<td className="px-2 py-2 text-center border bg-gray-50 font-semibold text-sm">
  {(() => {
    const attendance = days[person] || {};
    const values = Object.values(attendance);

    // ✅ Exclude "D/O" (Day Off) from calculations
    const validDays = values.filter(v => v !== "D/O");

    // ✅ Count totals
    const totalP = validDays.filter(v => v === "P").length;
    const totalA = validDays.filter(v => v === "A").length;
    const totalL = validDays.filter(v => v === "L").length; // ✅ New: count leaves

    const totalCount = validDays.length || 1;
    const percentage = ((totalP / totalCount) * 100).toFixed(1);

    // ✅ Color-code attendance %
    let color = "text-red-600";
    if (percentage >= 80) color = "text-green-600";
    else if (percentage >= 60) color = "text-yellow-600";

    return (
      <>
        <div>P: {totalP} | A: {totalA} | L: {totalL}</div>
        <div className={`font-bold ${color}`}>({percentage}%)</div>
      </>
    );
  })()}
</td>



          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}



   <style>{`

/* --- Modern Roster Table --- */
@media (min-width: 1400px) {
    .container, .container-lg, .container-md, .container-sm, .container-xl, .container-xxl {
        max-width: 1750px;
    }
}
/* --- L-Shape Hover Highlight --- */

/* Horizontal line (row to hovered column) */
.roster-table td.lshape-row {
  background: linear-gradient(to right, #dbeafe 98%, transparent);
}

/* Vertical line (column up to hovered row) */
.roster-table td.lshape-col,
.roster-table th.lshape-col {
  background: linear-gradient(to bottom, #dbeafe 98%, transparent);
}

/* Stronger color at intersection */
.roster-table td.lshape-intersect {
  background-color: #93c5fd !important;
}

/* Smooth transition */
.roster-table td,
.roster-table th {
  transition: background 0.15s ease;
}




.bulk-controls-container {
  background: #f9fafb;
  border: 1px solid #e0e0e0;
}

.roster-wrapper {
  background: #ffffff;
  border-radius: 16px;
  padding: 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  border: 1px solid #e5e7eb;
  overflow: hidden;
  width: 100%;
}

/* Table Base */
.roster-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;   /* keeps all columns equal */
  font-size: 11px;
}

/* Header */
.roster-table thead {
  background: #f9fafb;
  position: sticky;
  top: 0;
  z-index: 5;
}

.roster-table th {
  padding: 6px 2px;
  font-weight: 600;
  font-size: 11px;
  text-align: center;
  border-bottom: 2px solid #e5e7eb;
}

/* --- Calendar Pill for Days --- */
.roster-table th.day-pill {
  background: transparent;
  border: none;
}

.roster-table th.day-pill span {
  display: block;
  padding: 4px 6px;
  min-width: 22px;
  border-radius: 8px;
  background: #e0e7ff;
  color: #1e3a8a;
  font-size: 10px;
  font-weight: 600;
  transition: all 0.2s ease;
  cursor: default;
}

.roster-table th.day-pill span:hover {
  background: #2563eb;
  color: #fff;
}

/* Rows */
.roster-table td {
  border: 1px solid #e5e7eb;
  padding: 2px;
  text-align: center;
  vertical-align: middle;
  height: 24px;
}

/* Zebra & hover */
.roster-table tbody tr:nth-child(even) {
  background: #f9fafb;
}
.roster-table tbody tr:hover {
  background: #f3f4f6;
}

/* Sticky staff column */
.roster-table .staff-cell {
  font-weight: 600;
  color: #1d4ed8;
  text-align: left;
  padding-left: 6px;
  background: #f8fafc;
  position: sticky;
  left: 0;
  z-index: 10;
  min-width: 120px;
  border-right: 1px solid #e5e7eb;
}

/* Compact Dropdown */
.roster-table .status-dropdown .select__control {
  min-height: 18px !important;
  height: 18px !important;
  font-size: 9px !important;
  border-radius: 4px !important;
  border: 1px solid #d1d5db !important;
}

.roster-table .status-dropdown .select__dropdown-indicator,
.roster-table .status-dropdown .select__clear-indicator {
  padding: 0 2px !important;
}

.py-4{
padding-top: 1.5rem !important;
padding-bottom: 1.5rem !important;
}

/* Holiday label */
.roster-table .holiday-note {
  font-size: 7px;
  color: #dc2626;
  line-height: 1;
  margin-top: 1px;
}

/* --- Colored Badges --- */
.status-badge {
  display: inline-block;
  padding: 1px 4px;
  border-radius: 4px;
  font-size: 9px;
  font-weight: 600;
  color: #fff;
}
.status-E { background-color: #22c55e; }
.status-M { background-color: #3b82f6; }
.status-N { background-color: #a855f7; }
.status-O { background-color: #9ca3af; }


      `}</style>

    {/* Action Buttons */}
    <div className="d-flex flex-wrap gap-3 mb-5">
      <button className="btn btn-primary shadow-sm" onClick={downloadPDFs}>📥 Download PDFs</button>
      <button className="btn btn-success shadow-sm" onClick={saveRosterToDB}>💾 Save Roster to DB</button>
      <button className="btn btn-secondary shadow-sm" onClick={() => fetchRoster(null, month)}>📥 Load Saved Roster for {month}</button>
      
    </div>
    {/* 🧾 Upload Excel Attendance */}
<div className="mb-4">
  <label className="form-label fw-semibold text-gray-700">📤 Upload Attendance Excel</label>
  <input
    type="file"
    accept=".xlsx, .xls, .csv"
    className="form-control rounded-3 shadow-sm"
    onChange={handleExcelUpload}
  />
</div>


    {/* Ward Boy Summary */}
    <div className="mb-3">
      <label className="form-label fw-semibold text-gray-700">🔍 Select Ward Boy</label>
      <Select
        options={staff.map(name => ({ value: name, label: name }))}
        value={selectedStaff}
        onChange={handleStaffSelect}
        placeholder="Select ward boy..."
        isClearable
      />
      {selectedStaff && (
        <p className="fw-semibold mt-2">
          {selectedStaff.label} attended <span className="text-success">{eCount}</span> "E" days in {month}.
        </p>
      )}
    </div>
  </div>
);


};

export default DoctorsAttendance;
